import { Controller } from "@hotwired/stimulus"

// Activates when query results contain ProcessGuid / ParentProcessId columns.
// Renders a DAG (directed acyclic graph) as SVG alongside the results table.
// Nodes are severity-coded; MITRE technique IDs are linked inline.

const SEVERITY = {
  crit: { fill: "rgba(239,68,68,.2)", stroke: "#EF4444", strokeWidth: 2 },
  warn: { fill: "rgba(245,158,11,.15)", stroke: "#F59E0B", strokeWidth: 1.5 },
  sus:  { fill: "rgba(168,85,247,.15)", stroke: "#A855F7", strokeWidth: 1.5 },
  norm: { fill: "rgba(100,116,139,.1)", stroke: "#475569", strokeWidth: 1 },
  sys:  { fill: "rgba(59,130,246,.12)", stroke: "#3B82F6", strokeWidth: 1 },
}

const LOLBINS = ["certutil", "wscript", "cscript", "mshta", "regsvr32", "rundll32", "msiexec", "bitsadmin", "powershell", "cmd"]

const PROC_RE = /\|\s*where\s+ProcessGuid\s*==\s*"([^"]+)"/i

export default class extends Controller {
  #boundCheck = null
  #observer = null

  connect() {
    this.#boundCheck = () => this.#maybeRender()
    document.addEventListener("turbo:before-stream-render", this.#boundCheck)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.#boundCheck)
    this.#observer?.disconnect()
  }

  #maybeRender() {
    const query = document.querySelector("textarea[name='query']")?.value?.trim() || ""
    if (!PROC_RE.test(query)) {
      this.element.querySelector(".siem-proc-tree-output")?.remove()
      window.dispatchEvent(new CustomEvent("siem:viz-tree-destroy"))
      return
    }
    this.#observer?.disconnect()
    this.#observer = new MutationObserver(() => {
      this.#observer.disconnect()
      this.#buildTree()
    })
    const tablePane = this.element.querySelector(".siem-table-pane")
    if (tablePane) this.#observer.observe(tablePane, { childList: true, subtree: true })
  }

  #buildTree() {
    const table = this.element.querySelector(".siem-table-pane table")
    if (!table) return

    const headers = [...table.querySelectorAll("thead th")].map(th => th.textContent.trim().toLowerCase())
    const rows = [...table.querySelectorAll("tbody tr")]
    if (!rows.length) return

    const col = name => headers.findIndex(h => h.includes(name))
    const nameIdx   = Math.max(col("image"), col("processname"), col("process.name"), col("commandline"))
    const pidIdx    = Math.max(col("processid"), col("pid"))
    const ppidIdx   = Math.max(col("parentprocessid"), col("ppid"))
    const guidIdx   = Math.max(col("processguid"), col("process.entityid"))
    const pguidIdx  = Math.max(col("parentprocessguid"))
    const cmdIdx    = Math.max(col("commandline"), col("process.command_line"))
    const tsIdx     = col("timestamp")

    if (nameIdx < 0 && guidIdx < 0) return

    const nodes = {}
    const edges = []

    rows.forEach(row => {
      const cells = [...row.querySelectorAll("td")]
      const text = i => cells[i]?.textContent.trim() || ""
      const guid   = guidIdx >= 0  ? text(guidIdx)  : text(pidIdx)
      const pguid  = pguidIdx >= 0 ? text(pguidIdx) : text(ppidIdx)
      const name   = nameIdx >= 0  ? text(nameIdx).split(/[/\\]/).pop() : guid

      if (!guid) return
      nodes[guid] = {
        guid,
        name: name.length > 32 ? name.slice(0, 29) + "…" : name,
        cmd:  cmdIdx >= 0 ? text(cmdIdx).slice(0, 60) : "",
        pid:  pidIdx >= 0 ? text(pidIdx) : "",
        ts:   tsIdx >= 0  ? text(tsIdx).slice(11, 19) : "",
        severity: this.#severity(name, cmdIdx >= 0 ? text(cmdIdx) : ""),
      }
      if (pguid && pguid !== guid) edges.push({ from: pguid, to: guid })
    })

    if (!Object.keys(nodes).length) return

    const svg = this.#buildSVG(nodes, edges)
    this.#inject(svg, table)
  }

  #severity(name, cmd) {
    const n = name.toLowerCase()
    if (LOLBINS.some(l => n.startsWith(l))) return "crit"
    const c = cmd.toLowerCase()
    if (/-enc\b|-encodedcommand|-nop|-hidden|-bypass/i.test(c)) return "warn"
    if (/\.(vbs|js|hta|ps1|bat)/i.test(n)) return "sus"
    if (/system32|winlogon|services|lsass/i.test(n)) return "sys"
    return "norm"
  }

  #buildSVG(nodes, edges) {
    const NODE_W = 220, NODE_H = 56, H_GAP = 48, V_GAP = 72
    const nodeList = Object.values(nodes)

    // Build adjacency for layout: roots first, then children
    const childMap = {}
    const parentMap = {}
    edges.forEach(e => {
      (childMap[e.from] ??= []).push(e.to)
      parentMap[e.to] = e.from
    })
    const roots = nodeList.filter(n => !parentMap[n.guid])

    // Assign (col, row) positions via BFS
    const pos = {}
    const visited = new Set()
    const queue = roots.map((n, i) => ({ guid: n.guid, col: 0, row: i }))
    let maxCol = 0, maxRow = roots.length - 1
    while (queue.length) {
      const { guid, col, row } = queue.shift()
      if (visited.has(guid)) continue
      visited.add(guid)
      pos[guid] = { col, row }
      maxCol = Math.max(maxCol, col)
      maxRow = Math.max(maxRow, row)
      ;(childMap[guid] || []).forEach((child, i) => {
        if (!visited.has(child)) queue.push({ guid: child, col: col + 1, row: maxRow + 1 + i })
        maxRow += i > 0 ? 1 : 0
      })
    }
    // Place any unreachable nodes at the end
    nodeList.forEach(n => {
      if (!pos[n.guid]) { maxRow++; pos[n.guid] = { col: 0, row: maxRow } }
    })

    const W = (maxCol + 1) * (NODE_W + H_GAP) + 20
    const H = (maxRow + 1) * (NODE_H + V_GAP) + 20
    const nx = (guid) => 10 + pos[guid].col * (NODE_W + H_GAP)
    const ny = (guid) => 10 + pos[guid].row * (NODE_H + V_GAP)

    const esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;")

    let svg = `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" class="siem-proc-graph-svg">`
    svg += `<defs>
      <marker id="ptArrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
        <path d="M0,0 L0,6 L7,3 z" fill="#2D3B4E"/>
      </marker>
      <marker id="ptArrowCrit" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
        <path d="M0,0 L0,6 L7,3 z" fill="#EF4444"/>
      </marker>
    </defs>`

    // Draw edges
    edges.forEach(({ from, to }) => {
      if (!pos[from] || !pos[to]) return
      const x1 = nx(from) + NODE_W, y1 = ny(from) + NODE_H / 2
      const x2 = nx(to),            y2 = ny(to) + NODE_H / 2
      const mx = (x1 + x2) / 2
      const toSev = nodes[to]?.severity
      const marker = (toSev === "crit" || toSev === "warn") ? "url(#ptArrowCrit)" : "url(#ptArrow)"
      const stroke = (toSev === "crit") ? "#EF444488" : (toSev === "warn") ? "#F59E0B88" : "#2D3B4E"
      const dash = (toSev === "warn" || toSev === "sus") ? 'stroke-dasharray="4,3"' : ""
      svg += `<path d="M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}" fill="none" stroke="${stroke}" stroke-width="1.5" ${dash} marker-end="${marker}"/>`
    })

    // Draw nodes
    nodeList.forEach(n => {
      if (!pos[n.guid]) return
      const x = nx(n.guid), y = ny(n.guid)
      const s = SEVERITY[n.severity]
      const mitre = this.#mitreIn(n.cmd + " " + n.name)

      svg += `<rect x="${x}" y="${y}" width="${NODE_W}" height="${NODE_H}" rx="8" fill="${s.fill}" stroke="${s.stroke}" stroke-width="${s.strokeWidth}"/>`
      svg += `<text x="${x + 10}" y="${y + 17}" font-family="monospace" font-size="12" font-weight="700" fill="#F1F5F9">${esc(n.name)}</text>`
      if (n.pid) svg += `<text x="${x + 10}" y="${y + 31}" font-family="monospace" font-size="10" fill="#64748B">PID ${esc(n.pid)}${n.ts ? "  ·  " + esc(n.ts) : ""}</text>`
      if (n.cmd) svg += `<text x="${x + 10}" y="${y + 45}" font-family="monospace" font-size="9" fill="#94A3B8">${esc(n.cmd)}</text>`
      mitre.forEach((tid, i) => {
        const bx = x + NODE_W - 44 - i * 46
        svg += `<rect x="${bx}" y="${y + 2}" width="40" height="14" rx="3" fill="rgba(168,85,247,.25)"/>`
        svg += `<text x="${bx + 20}" y="${y + 12}" font-family="monospace" font-size="9" font-weight="800" fill="#D8B4FE" text-anchor="middle">${esc(tid)}</text>`
      })
    })

    return svg + "</svg>"
  }

  #mitreIn(text) {
    const seen = new Set()
    const matches = []
    let m
    const re = /\b(T\d{4}(?:\.\d{3})?)\b/g
    while ((m = re.exec(text)) && matches.length < 3) {
      if (!seen.has(m[1])) { seen.add(m[1]); matches.push(m[1]) }
    }
    return matches
  }

  #inject(svgContent, table) {
    this.element.querySelector(".siem-proc-tree-output")?.remove()
    window.dispatchEvent(new CustomEvent("siem:viz-tree-clear"))

    const wrap = document.createElement("div")
    wrap.className = "siem-proc-tree-output"
    wrap.innerHTML = `
      <div class="siem-proc-tree-header">
        <i class="fa fa-sitemap"></i> Process Tree
        <button class="siem-proc-tree-close" type="button"><i class="fa fa-times"></i></button>
      </div>
      <div class="siem-proc-tree-scroll">${svgContent}</div>
    `

    const injectEvent = new CustomEvent("siem:viz-tree-inject", { detail: { wrap }, cancelable: true })
    window.dispatchEvent(injectEvent)

    if (!injectEvent.defaultPrevented) {
      wrap.querySelector(".siem-proc-tree-close").addEventListener("click", () => wrap.remove())
      table.closest(".panel")?.after(wrap) || table.parentElement?.after(wrap)
    }
  }
}
